/**
 * Shared helpers of the batch tools (W2-11 batch-tools; DESIGN.md §5.8): message keys (namespace 'tools', English
 * text in the pure-data catalogue tools.messages.ts), number formatting for generated expressions, time-order sorting and the "does this
 * track already move inside a span" test used by Clone (skip policy) and Motion path (refuse policy).
 */

import type { KeyRec } from '../commands/kit';
import type { Key, Note } from '../model/types';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import type { MessageRef } from '../util/types';

/** Message keys produced by the tools (ns 'tools'; English text in tools.messages.ts). */
export const TOOLS_MSG = {
  // Distribute / Ramp
  distributeNothing: 'tools.distribute.nothing',
  distributeBadNumber: 'tools.distribute.badNumber',
  distributeNotNumeric: 'tools.distribute.notNumeric',
  distributeLastKey: 'tools.distribute.lastKey',
  distributeEaseSkipped: 'tools.distribute.easeSkipped',
  distributeClamped: 'tools.distribute.clamped',
  distributeEvalFailed: 'tools.distribute.evalFailed',
  distributeArriveAfterStay: 'tools.distribute.arriveAfterStay',
  distributeSkippedKeys: 'tools.distribute.skippedKeys',
  // Clone to lines
  cloneNothing: 'tools.clone.nothing',
  cloneOneLine: 'tools.clone.oneLine',
  cloneNoTargets: 'tools.clone.noTargets',
  cloneCount: 'tools.clone.count',
  cloneSkippedOverlap: 'tools.clone.skippedOverlap',
  cloneNotesOnUiMover: 'tools.clone.notesOnUiMover',
  cloneNotApplicable: 'tools.clone.notApplicable',
  cloneNewLayerFallback: 'tools.clone.newLayerFallback',
  cloneSourceRemoved: 'tools.clone.sourceRemoved',
  cloneTargetSkipped: 'tools.clone.targetSkipped',
  // Fill between
  fillNeedTwo: 'tools.fill.needTwo',
  fillSameLine: 'tools.fill.sameLine',
  fillSameTime: 'tools.fill.sameTime',
  fillBadSpacing: 'tools.fill.badSpacing',
  fillTooMany: 'tools.fill.tooMany',
  fillNone: 'tools.fill.none',
  fillTemplate: 'tools.fill.template',
  // Motion path
  pathRange: 'tools.path.range',
  pathExpr: 'tools.path.expr',
  pathEval: 'tools.path.eval',
  pathDensity: 'tools.path.density',
  pathTolerance: 'tools.path.tolerance',
  pathTooMany: 'tools.path.tooMany',
  pathOverlap: 'tools.path.overlap',
  pathNoFreeLayer: 'tools.path.noFreeLayer',
  pathLayer: 'tools.path.layer',
  pathLayerInvalid: 'tools.path.layerInvalid',
  pathFitted: 'tools.path.fitted',
  // Flatten
  flattenNotLayered: 'tools.flatten.notLayered',
  flattenOneLayer: 'tools.flatten.oneLayer',
  flattenTolerance: 'tools.flatten.tolerance',
  flattenExact: 'tools.flatten.exact',
  flattenBaked: 'tools.flatten.baked',
  flattenTooMany: 'tools.flatten.tooMany',
  flattenPartner: 'tools.flatten.partner',
  // Fit
  fitTolerance: 'tools.fit.tolerance',
  fitNothing: 'tools.fit.nothing',
  fitDone: 'tools.fit.done',
  fitNone: 'tools.fit.none',
  // Stagger and re-timing
  staggerNothing: 'tools.stagger.nothing',
  staggerZero: 'tools.stagger.zero',
  staggerSingle: 'tools.stagger.single',
  retimeCollide: 'tools.retime.collide',
  retimeBeforeStart: 'tools.retime.beforeStart',
  // Shared
  lineMissing: 'tools.lineMissing',
} as const;

export type ToolsMsgKey = (typeof TOOLS_MSG)[keyof typeof TOOLS_MSG];

export function toolReason(key: ToolsMsgKey, params?: Readonly<Record<string, string | number>>): MessageRef {
  return params === undefined ? { key } : { key, params };
}

/** A number as written in a generated expression ("-400", "0.25"; never "-0"). */
export function numText(x: number): string {
  return String(x === 0 ? 0 : x);
}

/** True when every value is finite. */
export function allFinite(values: readonly number[]): boolean {
  return values.every((v) => Number.isFinite(v));
}

/** Notes in time order (beat, X, id): the order expressions see as i = 0..n-1 (core/commands exprNotes). */
export function noteTimeOrder(a: Note, b: Note): number {
  return Beats.cmp(a.t, b.t) || a.x - b.x || a.id - b.id;
}

/** Notes in X order (X, beat, id). */
export function noteXOrder(a: Note, b: Note): number {
  return a.x - b.x || Beats.cmp(a.t, b.t) || a.id - b.id;
}

/** Keys in time order (beat, id): the order Distribute ramps keys in. */
export function keyTimeOrder(a: Pick<Key, 't' | 'id'>, b: Pick<Key, 't' | 'id'>): number {
  return Beats.cmp(a.t, b.t) || a.id - b.id;
}

/**
 * True when a track already moves inside the open span (s, e): a key strictly inside it, or a segment (a key with
 * a next key) that intersects it. A lone key or the value held after the last key is not motion.
 */
export function spanHasMotion(keys: readonly KeyRec[], s: Beat, e: Beat): boolean {
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i] as KeyRec;
    if (Beats.lt(s, k.t) && Beats.lt(k.t, e)) return true;
    const next = keys[i + 1];
    if (next !== undefined && Beats.lt(k.t, e) && Beats.lt(s, next.t)) return true;
  }
  return false;
}

/** Clamps x to [lo, hi]. */
export function clamp(x: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, x));
}

/** Tracks the earliest of several beats (the "jump to change" focus of a tool). */
export class EarliestBeat {
  beat: Beat | null = null;
  add(b: Beat): void {
    if (this.beat === null || Beats.lt(b, this.beat)) this.beat = b;
  }
}
